import {
  CheckCircle as CheckCircleIcon,
  ContentCopy as CopyIcon,
  LinkedIn as LinkedInIcon,
} from '@mui/icons-material';
import {
  Button,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import copy from 'copy-to-clipboard';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';
import { Account } from '../../../@types/Account';
import { CONFIG } from '../../../config';
import { DATE_FORMAT } from '../../../consts';
import { PricingPlan } from '../../../consts/enums';
import { useGetAccountByUserEmail } from '../../../hooks/query/useAccounts';
import {
  useAccountUsers,
  useUserActivationLink,
} from '../../../hooks/query/useUsers';
import { useGetCredits, useUpdateAccountPlan } from '../../../hooks/useAccount';
import useNavigate from '../../../hooks/useNavigate';
import STRING_KEYS from '../../../language/keys';
import { addDays } from '../../AccountsPage/formik';
import { Logo } from './AccountInfo.styled';

type AccountInfoProps = {
  account: Account;
  userEmail: string;
};

export const AccountInfo = ({
  account,
  userEmail,
}: AccountInfoProps): JSX.Element => {
  const { t: translate } = useTranslation();

  const {
    id: accountId,
    createdAt,
    displayName,
    subDomainName,
    allowedEmailDomain,
    plan,
    companyInfo,
    logo,
    accountStatus,
  } = account ?? {};

  const [trialEndDate, setTrialEndDate] = useState(plan?.endDate);

  const { data: credits } = useGetCredits(
    plan?.isCreditsBased ? accountId : null,
  );

  const updateAccountPlan = useUpdateAccountPlan();
  const { refetch: getAccountByUserEmail } = useGetAccountByUserEmail(
    userEmail,
  );

  const { goToChoosePlan } = useNavigate();

  const handleTrialExtend = async () => {
    await updateAccountPlan.mutateAsync({
      accountId,
      accountPlanId: plan?.id,
      plan: { endDate: trialEndDate },
    });

    await getAccountByUserEmail();
  };

  const {
    mutateAsync: getUserActivationLink,
    data: userActivationLink,
    isLoading: isGeneratingUserActivationLink,
  } = useUserActivationLink();

  const { data: accountUsers = [] } = useAccountUsers({
    accountId: account?.id,
  });

  const [isUserActivationLinkCopied, setIsUserActivationLinkCopied] = useState(
    false,
  );

  const handleGenerateUserActivationLink = async () => {
    const userId = accountUsers.find((user) => user.email?.toLowerCase()?.includes(userEmail?.toLowerCase()),
    )?.id;

    if (userId) {
      await getUserActivationLink({ userId });
    }
  };

  useEffect(() => {
    setIsUserActivationLinkCopied(false);
  }, [accountId, userEmail]);

  const renderPlanInfo = (): JSX.Element => {
    if (plan?.isCreditsBased) {
      return (
        <Stack>
          <Typography>
            <strong>User Seats Limit:</strong> {plan?.maxUserSeats}
          </Typography>

          {credits && (
            <Typography>
              <strong>Current Credits:</strong> {credits?.creditsUsed} /{' '}
              {credits?.totalCredits}
            </Typography>
          )}

          <Typography>
            <strong>Monthly Recurring Credits:</strong> {plan?.monthlyCredits}
          </Typography>

          <Typography>
            <strong>Next Credits Renewal Date:</strong>{' '}
            <Moment format={ DATE_FORMAT.DATE }>
              {plan?.nextCreditsRenewalDate}
            </Moment>
          </Typography>
        </Stack>
      );
    }

    return (
      <Typography>
        <strong>Positions Limit:</strong> {plan?.maximumOpenPositions}
      </Typography>
    );
  };

  return (
    <Stack spacing={ 3 }>
      <Grid container spacing={ 2 }>
        <Grid item xs={ 5.5 }>
          <Stack spacing={ 3 }>
            <Stack direction="row" spacing={ 1 } alignItems="center">
              <Logo src={ logo } alt="logo" />

              <Typography variant="h4">{displayName}</Typography>

              {companyInfo?.linkedinUrl && (
                <IconButton href={ companyInfo.linkedinUrl } target="_blank">
                  <LinkedInIcon />
                </IconButton>
              )}
            </Stack>

            <Stack>
              <Stack direction="row" spacing={ 1 } alignItems="center">
                <Typography>
                  <strong>ID:</strong> {accountId}
                </Typography>

                <IconButton onClick={ () => copy(accountId) } size="small">
                  <CopyIcon />
                </IconButton>
              </Stack>

              <Typography>
                <strong>Created at:</strong>{' '}
                <Moment format={ DATE_FORMAT.FULL_DATE }>{createdAt}</Moment>
              </Typography>

              <Typography>
                <strong>Status:</strong>{' '}
                {translate(STRING_KEYS.ACCOUNT_STATUSES[accountStatus])}
              </Typography>

              <Typography>
                <strong>Sub-domain:</strong> {subDomainName}
                {CONFIG.SUBDOMAIN_NAME}
              </Typography>

              <Typography>
                <strong>Email domain:</strong> @{allowedEmailDomain}
              </Typography>
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={ 1 } sx={ { display: 'flex', justifyContent: 'center' } }>
          <Divider orientation="vertical" />
        </Grid>

        <Grid item xs={ 5.5 }>
          <Stack spacing={ 3 }>
            <Typography variant="h5">
              <strong>Plan:</strong> {plan?.planDefinition?.displayName}
            </Typography>

            {renderPlanInfo()}

            <Stack direction="row" spacing={ 2 }>
              <Typography>
                <strong>Start date:</strong>{' '}
                <Moment format={ DATE_FORMAT.DATE }>{plan?.startDate}</Moment>
              </Typography>

              <Typography>
                <strong>End date:</strong>{' '}
                {plan?.endDate ? (
                  <Moment format={ DATE_FORMAT.DATE }>{plan?.endDate}</Moment>
                ) : (
                  'None'
                )}
              </Typography>
            </Stack>
          </Stack>
        </Grid>
      </Grid>

      <Stack direction="row" spacing={ 1 } alignSelf="flex-end">
        {account && (
          <Stack direction="row" spacing={ 1 } alignItems="flex-end">
            {userActivationLink?.link ? (
              <Button
                variant="outlined"
                onClick={ () => {
                  copy(userActivationLink.link);
                  setIsUserActivationLinkCopied(true);
                } }
              >
                {isUserActivationLinkCopied ? (
                  <>
                    <CheckCircleIcon />
                    Copied!
                  </>
                ) : (
                  <>
                    <CopyIcon />
                    Copy Activation Link
                  </>
                )}
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={ handleGenerateUserActivationLink }
                  disabled={ isGeneratingUserActivationLink }
                >
                  {isGeneratingUserActivationLink ?
                    'Generating...' :
                    'Generate Activation Link'}
                </Button>
              </>
            )}
          </Stack>
        )}

        <Button
          variant="contained"
          onClick={ () => goToChoosePlan(account.id, userEmail, false) }
        >
          Use this account
        </Button>
      </Stack>

      {plan?.planDefinition?.pricingPlan === PricingPlan.Trial && (
        <>
          <Divider />

          <Stack direction="row" spacing={ 2 } alignItems="center">
            <LocalizationProvider dateAdapter={ AdapterDateFns }>
              <DatePicker
                inputFormat="dd/MM/yyyy"
                label="Trial End Date"
                onChange={ (newValue) => setTrialEndDate(newValue ?? null) }
                value={ trialEndDate }
                renderInput={ (params) => <TextField { ...params } disabled /> }
                minDate={ addDays(new Date(), 1) }
              />
            </LocalizationProvider>

            <Button
              variant="outlined"
              onClick={ handleTrialExtend }
              disabled={ updateAccountPlan.isLoading }
            >
              Update Trial Period
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  );
};
